/**
 * Opening Leave Balance — one-time dedicated workflow.
 *
 * Lets an admin enter the CURRENT remaining leave-day balance for each
 * employee that existed BEFORE the HR system was deployed. The approval
 * is LOCKED per employee (one-time only), and future monthly accruals
 * continue to stack on top automatically.
 *
 * Route: /admin/opening-balance
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CalendarCheck, Lock, LockOpen, MagnifyingGlass, CaretLeft, CheckCircle,
  Warning, SpinnerGap, Info, Lightning, ArrowsClockwise,
} from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

export default function OpeningBalance() {
  const { language, direction } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const T = (a, e) => (isAr ? a : e);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all | pending | locked
  const [savingId, setSavingId] = useState(null);
  const [confirm, setConfirm] = useState(null); // {row, days}
  const [draftDays, setDraftDays] = useState({}); // user_id -> value
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await axios.get(`${API}/accruals/opening-balance/list`, { withCredentials: true });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to load';
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterStatus === 'pending' && r.opening_balance_set) return false;
      if (filterStatus === 'locked' && !r.opening_balance_set) return false;
      if (!q) return true;
      return (
        (r.name || '').toLowerCase().includes(q)
        || (r.name_en || '').toLowerCase().includes(q)
        || String(r.employee_number || '').toLowerCase().includes(q)
        || (r.department || '').toLowerCase().includes(q)
      );
    });
  }, [rows, search, filterStatus]);

  const stats = useMemo(() => ({
    total: rows.length,
    locked: rows.filter((r) => r.opening_balance_set).length,
    pending: rows.filter((r) => !r.opening_balance_set).length,
  }), [rows]);

  const openConfirm = (row) => {
    const raw = draftDays[row.user_id];
    const days = parseFloat(raw);
    if (!Number.isFinite(days) || days < 0) {
      toast.error(T('أدخل عدد أيام صحيح (0 أو أكثر)', 'Enter a valid days value (0 or more)'));
      return;
    }
    if (days > 400) {
      toast.error(T('القيمة أكبر من الحد المسموح (400 يوم)', 'Value exceeds limit (400 days)'));
      return;
    }
    setConfirm({ row, days });
  };

  const approve = async () => {
    if (!confirm) return;
    setSavingId(confirm.row.user_id);
    try {
      // Dual flow — the SAME confirm dialog drives both initial approval
      // and the one-time correction. The row's `opening_balance_set`
      // tells us which backend route to hit.
      const isCorrection = !!confirm.row.opening_balance_set;
      const url = isCorrection
        ? `${API}/accruals/opening-balance/${confirm.row.user_id}/correct`
        : `${API}/accruals/opening-balance/${confirm.row.user_id}`;
      await axios.post(url, { days: confirm.days }, { withCredentials: true });
      toast.success(
        isCorrection
          ? T('تم تصحيح الرصيد الافتتاحي (الفرصة الوحيدة)', 'Opening balance corrected (one-time)')
          : T('تم اعتماد الرصيد الافتتاحي', 'Opening balance approved')
      );
      setConfirm(null);
      setDraftDays((p) => { const n = { ...p }; delete n[confirm.row.user_id]; return n; });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || T('فشل الحفظ', 'Save failed'));
    } finally {
      setSavingId(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(isAr ? 'ar-SA' : 'en-GB'); }
    catch { return iso.slice(0, 10); }
  };

  const runReconcile = async () => {
    if (!window.confirm(T(
      'سيتم مراجعة كل الإجازات المعتمدة والمخالصات الموقعة التي لم يُخصم رصيدها بعد، وخصمها الآن (مع إظهار الرصيد السالب عند الحاجة). متابعة؟',
      'This will scan every approved leave + signed clearance whose balance consumption never applied, and apply it now (negative balances will become visible). Continue?',
    ))) return;
    setReconciling(true);
    setReconcileResult(null);
    try {
      const { data } = await axios.post(`${API}/accruals/reconcile-missing-consumptions`, {}, { withCredentials: true });
      setReconcileResult(data);
      toast.success(
        T(
          `تمت المعالجة: ${data.applied_leaves} إجازة + ${data.applied_clearances} مخالصة لـ ${data.affected_users_count} موظف`,
          `Done: ${data.applied_leaves} leaves + ${data.applied_clearances} clearances across ${data.affected_users_count} employees`,
        ),
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || T('فشلت المعالجة', 'Reconciliation failed'));
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA] dark:bg-slate-950" dir={direction} data-testid="opening-balance-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/leaves')}
            className="text-[#655dc6] hover:bg-[#EDE9FE]"
            data-testid="back-to-leaves-btn"
          >
            <CaretLeft size={18} className={isAr ? 'ms-1 rotate-180' : 'me-1'} />
            {T('رجوع', 'Back')}
          </Button>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Title */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-[#655dc6] dark:text-violet-300 flex items-center gap-2">
              <Lightning size={28} weight="duotone" />
              {T('الرصيد الافتتاحي للإجازات', 'Opening Leave Balance')}
            </h1>
            <p className="text-sm text-[#655dc6]/70 dark:text-slate-400 mt-1 max-w-3xl leading-relaxed">
              {T(
                'أدخل رصيد أيام الإجازة الحالي لكل موظف قديم اليوم. سيتم اعتماده كرصيد افتتاحي ويبدأ النظام بإضافة التراكم الشهري فوقه تلقائياً. الاعتماد يتم لمرة واحدة فقط لكل موظف ويُقفل نهائياً بعدها.',
                "Enter the current remaining leave-days for each legacy employee today. It will be locked in as their opening balance and monthly accruals continue to stack on top automatically. One-time per employee — final once locked.",
              )}
            </p>
          </div>
          <Button
            onClick={runReconcile}
            disabled={reconciling || loading}
            className="bg-gradient-to-r from-rose-500 to-amber-600 hover:from-rose-600 hover:to-amber-700 text-white shadow-sm gap-2 shrink-0"
            data-testid="reconcile-btn"
          >
            {reconciling
              ? <SpinnerGap size={18} className="animate-spin" />
              : <ArrowsClockwise size={18} weight="duotone" />}
            {T('مزامنة الخصومات المفقودة', 'Reconcile missing deductions')}
          </Button>
        </div>

        {reconcileResult && (
          <div className="rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/10 dark:border-emerald-800 p-3 text-sm" data-testid="reconcile-result">
            <div className="font-semibold text-emerald-800 dark:text-emerald-200 mb-1">
              {T('تمت المعالجة', 'Reconciliation done')}
            </div>
            <div className="text-emerald-700 dark:text-emerald-300 text-xs">
              {T(
                `تم خصم ${reconcileResult.applied_leaves} إجازة و ${reconcileResult.applied_clearances} مخالصة بأثر رجعي لـ ${reconcileResult.affected_users_count} موظف. أرصدة بعض الموظفين قد تظهر سالبة الآن، وهذا طبيعي — ستُغطى بالتراكم الشهري قبل تاريخ السفر.`,
                `Applied ${reconcileResult.applied_leaves} leaves and ${reconcileResult.applied_clearances} clearances retroactively across ${reconcileResult.affected_users_count} employees. Some balances will now show negative — this is expected and will be covered by monthly accruals before the travel date.`,
              )}
            </div>
          </div>
        )}

        {/* Info banner */}
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 dark:bg-amber-900/10 dark:border-amber-800 p-3 flex items-start gap-3" data-testid="opening-balance-info">
          <Warning size={20} weight="duotone" className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
            <strong>{T('تنبيه مهم:', 'Important:')}</strong>{' '}
            {T(
              'الاعتماد نهائي ولا يمكن تعديله بعد التأكيد. تأكّد من رقم الأيام قبل الاعتماد. يُحسب الراتب الحالي تلقائياً كقيمة لليوم الواحد لأغراض كشف الإجازة والمستحقات.',
              'Approval is final and cannot be edited afterward. Verify the days before confirming. The current salary is auto-used to value each day for payroll calculations.',
            )}
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-[#C4BFF0] dark:border-slate-700" data-testid="kpi-total">
            <CardContent className="pt-5 pb-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#655dc6]/10 flex items-center justify-center">
                <CalendarCheck size={24} weight="duotone" className="text-[#655dc6]" />
              </div>
              <div>
                <p className="text-sm text-[#655dc6]/60 dark:text-slate-400">{T('إجمالي الموظفين', 'Total Employees')}</p>
                <p className="text-2xl font-bold text-[#655dc6] dark:text-violet-300">{stats.total}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 dark:border-amber-800" data-testid="kpi-pending">
            <CardContent className="pt-5 pb-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <LockOpen size={24} weight="duotone" className="text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-amber-700/80 dark:text-amber-400">{T('بانتظار الاعتماد', 'Pending')}</p>
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{stats.pending}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 dark:border-emerald-800" data-testid="kpi-locked">
            <CardContent className="pt-5 pb-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Lock size={24} weight="duotone" className="text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-emerald-700/80 dark:text-emerald-400">{T('تم الاعتماد', 'Locked')}</p>
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{stats.locked}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card className="border-[#C4BFF0] dark:border-slate-700">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="text-lg text-[#655dc6] dark:text-violet-300">
                {T('قائمة الموظفين', 'Employees')}
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute top-1/2 -translate-y-1/2 start-2 text-[#655dc6]/60" />
                  <Input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={T('بحث بالاسم أو الرقم الوظيفي', 'Search by name or employee number')}
                    className="ps-8 w-full sm:w-[280px] bg-white dark:bg-slate-800 border-[#C4BFF0]"
                    data-testid="ob-search-input"
                  />
                </div>
                <div className="flex items-center gap-1" data-testid="ob-filter-group">
                  <Button
                    size="sm"
                    variant={filterStatus === 'all' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('all')}
                    className={filterStatus === 'all' ? 'bg-[#655dc6] hover:bg-[#544cb0]' : 'border-[#C4BFF0] text-[#655dc6]'}
                    data-testid="ob-filter-all"
                  >
                    {T('الكل', 'All')} ({stats.total})
                  </Button>
                  <Button
                    size="sm"
                    variant={filterStatus === 'pending' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('pending')}
                    className={filterStatus === 'pending' ? 'bg-amber-600 hover:bg-amber-700' : 'border-amber-300 text-amber-700'}
                    data-testid="ob-filter-pending"
                  >
                    {T('بانتظار', 'Pending')} ({stats.pending})
                  </Button>
                  <Button
                    size="sm"
                    variant={filterStatus === 'locked' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('locked')}
                    className={filterStatus === 'locked' ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-300 text-emerald-700'}
                    data-testid="ob-filter-locked"
                  >
                    {T('معتمد', 'Locked')} ({stats.locked})
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <SpinnerGap size={28} className="animate-spin text-[#655dc6]" />
              </div>
            ) : loadError ? (
              <div className="text-center py-10" data-testid="ob-error">
                <div className="inline-flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                  <Warning size={18} weight="duotone" />
                  <span className="text-sm">{loadError}</span>
                </div>
                <div className="mt-3">
                  <Button size="sm" onClick={load} variant="outline" className="border-[#C4BFF0] text-[#655dc6]" data-testid="ob-retry">
                    {T('إعادة المحاولة', 'Retry')}
                  </Button>
                </div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 text-[#655dc6]/60 text-sm" data-testid="ob-empty">
                {T('لا توجد نتائج', 'No results')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="ob-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-start">{T('الرقم', 'No.')}</TableHead>
                      <TableHead className="text-start">{T('الموظف', 'Employee')}</TableHead>
                      <TableHead className="text-start">{T('الإدارة', 'Department')}</TableHead>
                      <TableHead className="text-center">{T('الراتب الحالي', 'Current Salary')}</TableHead>
                      <TableHead className="text-center">{T('الرصيد الحالي', 'Current Balance')}</TableHead>
                      <TableHead className="text-center">{T('الرصيد الافتتاحي', 'Opening Balance')}</TableHead>
                      <TableHead className="text-center">{T('إجراء', 'Action')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((r) => {
                      const locked = !!r.opening_balance_set;
                      const draftRaw = draftDays[r.user_id] ?? '';
                      return (
                        <TableRow
                          key={r.user_id}
                          className={locked ? 'bg-emerald-50/40 dark:bg-emerald-900/10' : ''}
                          data-testid={`ob-row-${r.user_id}`}
                        >
                          <TableCell className="font-mono text-xs text-[#655dc6]/80">
                            {r.employee_number || '—'}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-[#655dc6] dark:text-violet-300">{isAr ? r.name : (r.name_en || r.name)}</div>
                            {isAr && r.name_en && (
                              <div className="text-[10px] text-gray-500 dark:text-gray-400">{r.name_en}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-gray-700 dark:text-gray-300">
                            {r.department || '—'}
                          </TableCell>
                          <TableCell className="text-center font-mono text-sm">
                            {r.monthly_salary ? r.monthly_salary.toLocaleString() : '—'}{' '}
                            <span className="text-[10px] text-gray-500">{T('ر.س', 'SAR')}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`font-mono font-semibold ${(r.current_balance_days ?? 0) < 0 ? 'text-red-600' : 'text-[#655dc6]'}`}>
                              {r.current_balance_days ?? 0}
                            </span>
                            <span className="text-[10px] text-gray-500 ms-1">{T('يوم', 'd')}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            {locked ? (
                              <div className="inline-flex flex-col items-center gap-0.5" data-testid={`ob-locked-${r.user_id}`}>
                                <span className={`inline-flex items-center gap-1 ${r.opening_balance_corrected ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300' : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300'} px-2 py-0.5 rounded font-mono text-sm font-semibold`}>
                                  <Lock size={12} weight="fill" />
                                  {r.opening_balance_days ?? 0} {T('يوم', 'd')}
                                </span>
                                <span className="text-[10px] text-emerald-700/80 dark:text-emerald-400">
                                  {fmtDate(r.opening_balance_set_at)}
                                  {r.opening_balance_set_by_name && ` · ${r.opening_balance_set_by_name}`}
                                </span>
                                {r.opening_balance_corrected && r.opening_balance_original_days != null && (
                                  <span className="text-[10px] text-amber-700/90 dark:text-amber-400" title={T('القيمة قبل التصحيح', 'Pre-correction value')}>
                                    {T('قبل التصحيح', 'Was')}: <span className="font-mono">{r.opening_balance_original_days}</span>
                                  </span>
                                )}
                              </div>
                            ) : (
                              <Input
                                type="number"
                                step="0.5"
                                min="0"
                                max="400"
                                value={draftRaw}
                                onChange={(e) => setDraftDays((p) => ({ ...p, [r.user_id]: e.target.value }))}
                                placeholder="0"
                                className="w-24 text-center font-mono mx-auto border-amber-300 focus:border-amber-500"
                                data-testid={`ob-input-${r.user_id}`}
                                dir="ltr"
                              />
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {locked ? (
                              <div className="inline-flex flex-col items-center gap-1">
                                <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 text-xs font-medium">
                                  <CheckCircle size={14} weight="fill" />
                                  {T('مُعتمد', 'Approved')}
                                </span>
                                {!r.opening_balance_corrected ? (
                                  // One-time correction affordance (v1.6.30). Opens the SAME
                                  // confirm dialog pre-filled with the current days — the
                                  // `approve` handler routes to `/correct` based on the
                                  // row's `opening_balance_set` flag.
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setConfirm({ row: r, days: r.opening_balance_days ?? 0, mode: 'correct' })}
                                    className="h-6 px-2 text-[11px] border-amber-400 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                    data-testid={`ob-correct-${r.user_id}`}
                                    title={T('فرصة تصحيح وحيدة', 'One-time correction')}
                                  >
                                    <LockOpen size={12} weight="bold" className="me-1" />
                                    {T('تصحيح', 'Correct')}
                                  </Button>
                                ) : (
                                  <span className="text-[10px] text-gray-500 dark:text-gray-400" title={T('استُخدمت فرصة التصحيح', 'Correction already used')}>
                                    {T('قفل نهائي', 'Final')}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => openConfirm(r)}
                                disabled={savingId === r.user_id || !draftRaw}
                                className="h-8 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid={`ob-approve-${r.user_id}`}
                              >
                                {savingId === r.user_id
                                  ? <SpinnerGap size={14} className="animate-spin" />
                                  : T('اعتماد', 'Approve')}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Confirmation Dialog — dual-mode (approve + correct) */}
      <Dialog open={!!confirm} onOpenChange={(open) => { if (!open) setConfirm(null); }}>
        <DialogContent className="max-w-md" dir={direction} data-testid="ob-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Warning size={22} weight="duotone" />
              {confirm?.mode === 'correct'
                ? T('تصحيح الرصيد الافتتاحي (مرة واحدة)', 'Correct Opening Balance (one-time)')
                : T('تأكيد الاعتماد النهائي', 'Confirm Final Approval')}
            </DialogTitle>
            <DialogDescription className="pt-2 leading-relaxed">
              {confirm?.mode === 'correct'
                ? T(
                    'هذه فرصتك الوحيدة لتصحيح الرصيد الافتتاحي لهذا الموظف. بعد الحفظ لن يمكن تعديله مرة أخرى. تاريخ الإغلاق الأصلي يبقى كما هو ويستمر الاستحقاق اليومي من ذلك التاريخ.',
                    "This is your only chance to correct this employee's opening balance. After saving it cannot be edited again. The original closing date stays intact and the daily accrual continues from it.",
                  )
                : T(
                    'الاعتماد نهائي ولا يمكن تعديله لاحقاً. راجع البيانات قبل التأكيد:',
                    'This approval is final and cannot be edited afterward. Review the data before confirming:',
                  )}
            </DialogDescription>
          </DialogHeader>

          {confirm && (
            <div className="space-y-3 py-2 text-sm">
              <div className="rounded-lg bg-[#F8F7FC] dark:bg-slate-800 border border-[#C4BFF0] dark:border-slate-700 p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-[#655dc6]/70">{T('الموظف', 'Employee')}</span>
                  <span className="font-semibold text-[#655dc6] dark:text-violet-300">
                    {isAr ? confirm.row.name : (confirm.row.name_en || confirm.row.name)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#655dc6]/70">{T('الرقم الوظيفي', 'Employee No.')}</span>
                  <span className="font-mono">{confirm.row.employee_number || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#655dc6]/70">{T('الراتب الحالي (للتقييم)', 'Current Salary (valuation)')}</span>
                  <span className="font-mono">{(confirm.row.monthly_salary || 0).toLocaleString()} {T('ر.س', 'SAR')}</span>
                </div>
                {confirm.mode === 'correct' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-[#655dc6]/70">{T('الرصيد الحالي (قبل التصحيح)', 'Current (pre-correction)')}</span>
                      <span className="font-mono text-amber-700">{confirm.row.opening_balance_days ?? 0} {T('يوم', 'd')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#655dc6]/70">{T('تاريخ الإغلاق الأصلي', 'Original closing date')}</span>
                      <span className="font-mono text-xs">{fmtDate(confirm.row.opening_balance_set_at)}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 p-4 text-center">
                <div className="text-xs text-amber-700 dark:text-amber-300 mb-1">
                  {confirm.mode === 'correct'
                    ? T('الرصيد الجديد الصحيح', 'New correct balance')
                    : T('رصيد الأيام الذي سيُعتمد', 'Days to approve')}
                </div>
                {confirm.mode === 'correct' ? (
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="400"
                    value={confirm.days}
                    onChange={(e) => setConfirm((p) => ({ ...p, days: Number(e.target.value) }))}
                    className="w-32 text-center font-mono mx-auto text-2xl font-bold text-amber-800 bg-white border-amber-400"
                    dir="ltr"
                    data-testid="ob-correct-input"
                  />
                ) : (
                  <div className="text-3xl font-bold text-amber-800 dark:text-amber-200 font-mono">
                    {confirm.days} <span className="text-base font-normal">{T('يوم', 'days')}</span>
                  </div>
                )}
              </div>

              <div className="flex items-start gap-2 text-[11px] text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded p-2">
                <Info size={14} className="flex-shrink-0 mt-0.5 text-blue-600" />
                <div className="leading-relaxed">
                  {confirm.mode === 'correct'
                    ? T(
                        'سيتم حذف شرائح الاستحقاق الحالية وإعادة بنائها بالقيمة الجديدة، مع الحفاظ على تاريخ الإغلاق الأصلي كمرجع للاستحقاق اليومي.',
                        'Current accrual slices will be rebuilt with the new value while keeping the original closing date as the daily-accrual anchor.',
                      )
                    : T(
                        'بعد الاعتماد: التراكم الشهري للإجازة سيُضاف تلقائياً فوق هذا الرصيد ابتداءً من الشهر الحالي، ولن تظهر هذه الصفحة الموظف مرة أخرى للتعديل.',
                        'After approval: Monthly leave accruals stack on top from this month onwards, and this employee will no longer appear as editable here.',
                      )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid="ob-confirm-cancel">
              {T('إلغاء', 'Cancel')}
            </Button>
            <Button
              onClick={approve}
              disabled={savingId !== null || (confirm?.mode === 'correct' && (!Number.isFinite(confirm?.days) || confirm.days < 0))}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="ob-confirm-approve"
            >
              {savingId
                ? <SpinnerGap size={16} className="animate-spin me-1" />
                : <Lock size={16} weight="bold" className="me-1" />}
              {confirm?.mode === 'correct'
                ? T('تصحيح وقفل نهائي', 'Correct & Lock')
                : T('اعتماد وقفل نهائي', 'Confirm & Lock')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
